<template>
  <div>
    <ejs-dialog
      ref="reservationMoveAllPopup"
      :header="'일괄예약이동'"
      :allowDragging="true"
      :showCloseIcon="true"
      width="900"
      height="780"
      :animationSettings="{ effect: 'None' }"
      :isModal="true"
      :close="onReservationMoveAllPopupClosed"
    >
      <div class="window reservationMoveAll">
        <div class="windowContent">
          <div class="content-wrapper">
            <div class="content-lookup">
              <div class="lookup-left">
                <ul class="lookup-condition">
                  <li class="field">
                    <div class="title">예약일자</div>
                    <ul class="content">
                      <li class="item dateRange">
                        <input-date-range
                          v-model="moveReservationDateRange"
                          type="lookup-condition"
                          @change="search"
                        />
                      </li>
                    </ul>
                  </li>
                </ul>
                <div class="lookup-lookup">
                  <erp-button
                    button-div="GET"
                    :is-shortcut-button="true"
                    @click.native="search"
                  >
                    조회
                  </erp-button>
                </div>
              </div>
            </div>
            <div class="content-body">
              <article class="body-article">
                <!-- 이동 대상 요약 -->
                <section class="article-section section-01">
                  <div class="section-body">
                    <div class="body-box">
                      <ul>
                        <li>단체명 : {{ popupData.grpName }}</li>
                        <li>예약자명 : {{ popupData.resveName }}</li>
                        <li>이동 팀수 : {{ teamCount }}팀</li>
                      </ul>
                      <ul>
                        <li>현재 : {{ originSummary }}</li>
                      </ul>
                    </div>
                  </div>
                </section>
                <!-- 배치 가능 구간 -->
                <section class="article-section section-02">
                  <div class="section-header">
                    <div class="header-left">
                      <div class="header-title">
                        배치 가능 구간 ({{ originShapeCaption }})
                      </div>
                    </div>
                    <div class="header-right">
                      <div class="header-caption">
                        [{{ movableBlocks.length }}건]
                      </div>
                    </div>
                  </div>
                  <div class="section-body">
                    <ejs-grid-wrapper
                      ref="movableBlockGrid"
                      :provides="movableBlockGridProvides"
                      :columns="movableBlockGridColumns"
                      :dataSource="movableBlocks"
                      :allowResizing="true"
                      :isSelectedRowRetain="false"
                      @rowSelected="onMovableBlockSelected"
                    />
                  </div>
                </section>
                <!-- 이동 미리보기 -->
                <section class="article-section section-03">
                  <div class="section-header">
                    <div class="header-left">
                      <div class="header-title">이동 미리보기</div>
                    </div>
                  </div>
                  <div class="section-body">
                    <ejs-grid-wrapper
                      ref="movePreviewGrid"
                      :provides="movePreviewGridProvides"
                      :columns="movePreviewGridColumns"
                      :dataSource="movePreviewRecords"
                      :allowResizing="true"
                    />
                  </div>
                </section>
              </article>
            </div>
          </div>
        </div>
        <div class="windowFooter">
          <ul class="button">
            <li class="save keyColor">
              <erp-button
                button-div="SAVE"
                @click.native="saveReservationMoveAllPopup()"
              >
                일괄이동
              </erp-button>
            </li>
            <li class="close">
              <erp-button
                button-div="CLOSE"
                @click.native="closeReservationMoveAllPopup()"
              >
                닫기
              </erp-button>
            </li>
          </ul>
        </div>
      </div>
    </ejs-dialog>
  </div>
</template>

<script>
import commonMixin from "@/views/layout/mixin/commonMixin";
import confirmDialogMixin from "@/views/layout/mixin/messagePopupDialogMixin";
import { Resize } from "@syncfusion/ej2-vue-grids";
import { sortBy as _sortBy } from "lodash";
import moment from "moment";

import { getDayOfWeekAndCaption, getDayOfWeekCaption } from "@/utils/date";
import {
  commonCodesGetComName,
  commonCodesGetCommonCodeAttrbNameByCodeAndIdx,
  commonCodesGetStandardInfo,
} from "@/utils/commonCodes";
import EjsGridWrapper from "@/components/common/EjsGridWrapper";
import InputDateRange from "@/components/common/datetime/InputDateRange";
import GolfErpAPI from "@/api/v2/GolfErpAPI";
import ErpButton from "@/components/button/ErpButton.vue";

export default {
  name: "ReservationMoveAllPopup",
  mixins: [commonMixin, confirmDialogMixin],
  components: {
    EjsGridWrapper,
    InputDateRange,
    ErpButton,
  },
  data: function () {
    return {
      // { grpName, resveName, timeLists: [ 예약타임 목록 전체 ] }
      popupData: {
        grpName: null,
        resveName: null,
        timeLists: [],
      },
      moveOption: {
        resveDateFrom: null,
        resveDateTo: null,
      },
      // 조회한 전체 타임(점유 포함). 연속성 판정에 반드시 필요하다.
      reservationTimes: [],
      movableBlocks: [],
      selectedBlock: null,
      movableBlockGridProvides: [Resize],
      movePreviewGridProvides: [Resize],
    };
  },
  computed: {
    moveReservationDateRange: {
      get: function () {
        return {
          from: this.moveOption.resveDateFrom,
          to: this.moveOption.resveDateTo,
        };
      },
      set: function (moveReservationDateRange) {
        this.moveOption.resveDateFrom = moveReservationDateRange.from;
        this.moveOption.resveDateTo = moveReservationDateRange.to;
      },
    },
    originTimes() {
      return _sortBy(this.popupData.timeLists || [], [
        "resveCourse",
        "resveDate",
        "resveTime",
      ]);
    },
    teamCount() {
      return this.originTimes.length;
    },
    // 원본을 코스별로 묶는다. 이동 후에도 이 구성(코스별 팀수)을 그대로 유지한다.
    // 예) 어등 3팀 + 송정 3팀 -> 대상도 어등 3팀 + 송정 3팀
    originGroups() {
      const groupMap = new Map();

      this.originTimes.forEach((timeInfo) => {
        if (!groupMap.has(timeInfo.resveCourse)) {
          groupMap.set(timeInfo.resveCourse, {
            resveCourse: timeInfo.resveCourse,
            courseName: commonCodesGetComName(
              "COURSE_CODE",
              timeInfo.resveCourse
            ),
            times: [],
          });
        }

        groupMap.get(timeInfo.resveCourse).times.push(timeInfo);
      });

      const groups = Array.from(groupMap.values());

      groups.forEach((group) => {
        group.times = _sortBy(group.times, ["resveDate", "resveTime"]);
      });

      return groups;
    },
    originShapeCaption() {
      return this.originGroups
        .map((group) => `${group.courseName} ${group.times.length}팀`)
        .join(" + ");
    },
    originDate() {
      const resveDates = this.originTimes
        .map((timeInfo) => timeInfo.resveDate)
        .filter(Boolean);

      return resveDates.length > 0 ? resveDates.slice().sort()[0] : null;
    },
    // 코스 중 가장 이른 시작 시각. 코스 간 간격을 재는 기준점이 된다.
    // 예) 송정 07:07 / 어등 07:14 로 시작하면 기준은 07:07
    originAnchorTime() {
      const startTimes = this.originGroups
        .map((group) => group.times[0] && group.times[0].resveTime)
        .filter(Boolean);

      return startTimes.length > 0 ? startTimes.slice().sort()[0] : null;
    },
    originTimeIds() {
      return this.originTimes.map((timeInfo) => timeInfo.timeId);
    },
    originSummary() {
      if (this.originGroups.length === 0) {
        return "";
      }

      const resveDate = this.originGroups[0].times[0].resveDate;

      const courseCaptions = this.originGroups.map((group) => {
        const first = group.times[0];
        const last = group.times[group.times.length - 1];

        return `${group.courseName} ${first.resveTime} ~ ${last.resveTime}`;
      });

      return `${getDayOfWeekAndCaption(resveDate)}  ${courseCaptions.join(
        " / "
      )}`;
    },
    movableBlockGridColumns() {
      return [
        {
          field: "resveDateCaption",
          headerText: "일자",
          type: "string",
          minWidth: 16,
          width: 170,
          textAlign: "center",
        },
        {
          field: "startTime",
          headerText: "기준시간",
          type: "string",
          minWidth: 16,
          width: 90,
          textAlign: "center",
        },
        {
          field: "shapeCaption",
          headerText: "구성",
          type: "string",
          minWidth: 16,
          textAlign: "left",
        },
      ];
    },
    movePreviewGridColumns() {
      return [
        {
          field: "courseName",
          headerText: "코스",
          type: "string",
          minWidth: 16,
          width: 90,
          textAlign: "center",
        },
        {
          field: "fromCaption",
          headerText: "현재",
          type: "string",
          minWidth: 16,
          textAlign: "center",
        },
        {
          field: "toCaption",
          headerText: "이동 후",
          type: "string",
          minWidth: 16,
          textAlign: "center",
        },
      ];
    },
    // 원본 1팀 <-> 대상 1팀. 코스 그룹 순서와 그룹 내 순서를 그대로 맞춘다.
    movePairs() {
      if (!this.selectedBlock) {
        return [];
      }

      const pairs = [];

      this.originGroups.forEach((group, groupIdx) => {
        const targetGroup = this.selectedBlock.courses[groupIdx];

        if (!targetGroup) {
          return;
        }

        group.times.forEach((origin, idx) => {
          const target = targetGroup.times[idx];

          if (target) {
            pairs.push({ courseName: group.courseName, origin, target });
          }
        });
      });

      return pairs;
    },
    movePreviewRecords() {
      // NO 컬럼은 EjsGridWrapper 가 자동으로 붙여준다(isNOColumnDisplay 기본 true).
      return this.movePairs.map((pair) => ({
        courseName: pair.courseName,
        fromCaption: `${pair.origin.resveDate} ${pair.origin.resveTime}`,
        toCaption: `${pair.target.resveDate} ${pair.target.resveTime}`,
      }));
    },
  },
  methods: {
    popupOpen: function (popupData, resveDateFrom, resveDateTo) {
      this.popupData = popupData;
      this.moveOption.resveDateFrom = resveDateFrom;
      this.moveOption.resveDateTo = resveDateTo;

      this.search();
      this.$refs.reservationMoveAllPopup.show();
    },
    closeReservationMoveAllPopup: function () {
      this.$refs.reservationMoveAllPopup.hide();
    },
    onReservationMoveAllPopupClosed: function () {
      this.$emit("popupClosed");
    },
    async search() {
      this.selectedBlock = null;
      this.movableBlocks = [];

      // 코스 간 간격은 원본 일자의 타임테이블로 계산하므로,
      // 조회 범위가 원본 일자를 벗어나도 원본 일자는 항상 같이 받아온다.
      const originDate = this.originDate;
      let fetchDateFrom = this.moveOption.resveDateFrom;
      let fetchDateTo = this.moveOption.resveDateTo;

      if (originDate) {
        if (!fetchDateFrom || originDate < fetchDateFrom) {
          fetchDateFrom = originDate;
        }

        if (!fetchDateTo || originDate > fetchDateTo) {
          fetchDateTo = originDate;
        }
      }

      try {
        // blankFlag/aditTeamFlag 를 모두 포함해 타임테이블 전체를 받는다.
        // 점유·공백 타임이 보여야 "연속인지"를 판정할 수 있다.
        this.reservationTimes = await GolfErpAPI.fetchReservationTimes({
          resveDate: fetchDateFrom,
          resveDateTo: fetchDateTo,
          blankFlag: true,
          aditTeamFlag: true,
          isMemoSelect: false,
          isPriceView: false,
        });
      } catch (e) {
        console.error("ReservationMoveAllPopup.search.err ===>", e);
        this.reservationTimes = [];
        return;
      }

      this.scanMovableBlocks();
    },
    // 이동 가능한 타임인지 판정.
    // 원본 타임은 이동과 동시에 비워지므로 빈 타임으로 간주한다.
    isTimeAvailable(timeInfo, originTimeIdSet) {
      if (originTimeIdSet.has(timeInfo.timeId)) {
        return true;
      }

      return timeInfo.timeStatus === "EMPTY" && !timeInfo.webOpenFlag;
    },
    // 코스별 타임 목록을 (일자 + 코스) 키로 묶어 시간순 정렬한다.
    buildTimeMap() {
      const timeMap = new Map();

      (this.reservationTimes || []).forEach((timeInfo) => {
        const key = `${timeInfo.resveDate}|${timeInfo.resveCourse}`;

        if (!timeMap.has(key)) {
          timeMap.set(key, []);
        }

        timeMap.get(key).push(timeInfo);
      });

      timeMap.forEach((times, key) => {
        timeMap.set(key, _sortBy(times, ["resveTime"]));
      });

      return timeMap;
    },
    // 원본에서 각 코스가 기준시각(가장 이른 시작)보다 몇 칸 뒤에서 시작하는지 구한다.
    // 예) 송정 07:07 / 어등 07:14 (한 칸 뒤) -> 송정 0, 어등 +1
    //     이 간격을 이동 후에도 그대로 유지해야 끝나는 시각이 원본처럼 맞춰진다.
    buildCourseOffsets(timeMap) {
      const courseOffsets = new Map();
      const anchorTime = this.originAnchorTime;

      this.originGroups.forEach((group) => {
        const firstTime = group.times[0];
        const courseTimes = timeMap.get(
          `${firstTime.resveDate}|${group.resveCourse}`
        );

        if (!courseTimes) {
          courseOffsets.set(group.resveCourse, 0);
          return;
        }

        const anchorIdx = courseTimes.findIndex(
          (t) => t.resveTime === anchorTime
        );
        const startIdx = courseTimes.findIndex(
          (t) => t.timeId === firstTime.timeId
        );

        courseOffsets.set(
          group.resveCourse,
          anchorIdx >= 0 && startIdx >= 0 ? startIdx - anchorIdx : 0
        );
      });

      return courseOffsets;
    },
    // 원본의 코스 구성을 그대로 유지한 채 옮길 수 있는 구간을 찾는다.
    // - 코스별 팀수 유지 (어등 3 + 송정 4 -> 어등 3 + 송정 4)
    // - 코스 간 시작 간격도 유지 (어등이 한 칸 뒤였으면 이동 후에도 한 칸 뒤)
    // - 각 코스 안에서는 연속. 중간에 점유 타임이 끼면 후보에서 제외
    // - 부별(PART_DIV) 경계는 넘지 않음
    scanMovableBlocks() {
      const groups = this.originGroups;

      if (groups.length === 0) {
        this.movableBlocks = [];
        return;
      }

      const originTimeIdSet = new Set(this.originTimeIds);
      const timeMap = this.buildTimeMap();
      const courseOffsets = this.buildCourseOffsets(timeMap);

      // 기준 코스 = 원본에서 가장 이른 시각에 시작하는 코스
      const anchorGroup =
        groups.find(
          (group) => group.times[0].resveTime === this.originAnchorTime
        ) || groups[0];

      // 후보 일자는 사용자가 지정한 조회 범위 안으로 제한한다.
      // (원본 일자는 간격 계산용으로만 받아왔을 수 있다)
      const dateFrom = this.moveOption.resveDateFrom;
      const dateTo = this.moveOption.resveDateTo;

      const resveDates = Array.from(
        new Set((this.reservationTimes || []).map((t) => t.resveDate))
      )
        .filter(
          (resveDate) =>
            (!dateFrom || resveDate >= dateFrom) &&
            (!dateTo || resveDate <= dateTo)
        )
        .sort();

      const blocks = [];

      resveDates.forEach((resveDate) => {
        // 기준시각 후보는 기준 코스의 타임테이블에서 가져온다.
        const baseTimes = timeMap.get(
          `${resveDate}|${anchorGroup.resveCourse}`
        );

        if (!baseTimes) {
          return;
        }

        baseTimes.forEach((baseTime) => {
          const startTime = baseTime.resveTime;
          const pickedCourses = [];

          const matched = groups.every((group) => {
            const courseTimes = timeMap.get(
              `${resveDate}|${group.resveCourse}`
            );

            if (!courseTimes) {
              return false;
            }

            // 기준시각이 이 코스에도 존재해야 간격을 적용할 수 있다.
            const anchorIdx = courseTimes.findIndex(
              (t) => t.resveTime === startTime
            );

            if (anchorIdx < 0) {
              return false;
            }

            // 원본의 코스 간 간격만큼 뒤로 밀어서 시작한다.
            const startIdx =
              anchorIdx + (courseOffsets.get(group.resveCourse) || 0);

            if (startIdx < 0) {
              return false;
            }

            const window = courseTimes.slice(
              startIdx,
              startIdx + group.times.length
            );

            if (window.length < group.times.length) {
              return false;
            }

            if (
              !window.every((t) => this.isTimeAvailable(t, originTimeIdSet))
            ) {
              return false;
            }

            // 부별 경계는 넘지 않는다.
            const partDiv = window[0].partDiv;

            if (!window.every((t) => t.partDiv === partDiv)) {
              return false;
            }

            pickedCourses.push({
              resveCourse: group.resveCourse,
              courseName: group.courseName,
              partDiv: partDiv,
              times: window,
            });

            return true;
          });

          if (!matched) {
            return;
          }

          blocks.push({
            blockKey: `${resveDate}|${startTime}`,
            resveDate: resveDate,
            resveDateCaption: getDayOfWeekAndCaption(resveDate),
            startTime: startTime,
            shapeCaption: pickedCourses
              .map(
                (course) =>
                  `${course.courseName} ${course.times[0].resveTime}~${
                    course.times[course.times.length - 1].resveTime
                  }`
              )
              .join(" / "),
            courses: pickedCourses,
          });
        });
      });

      this.movableBlocks = _sortBy(blocks, ["resveDate", "startTime"]);
    },
    onMovableBlockSelected(args) {
      this.selectedBlock = args.data;
    },
    async saveReservationMoveAllPopup() {
      if (!this.selectedBlock) {
        this.errorToast("이동하실 구간을 선택해 주십시오");
        return;
      }

      const pairs = this.movePairs;

      if (pairs.length !== this.teamCount) {
        this.errorToast(
          "자료가 변경되었습니다. 자료를 조회 후 다시 작업 바랍니다."
        );
        return;
      }

      // 동반자 수가 대상 타임의 내장인원을 넘으면 서버에서 거부되므로 미리 막는다.
      for (let i = 0; i < pairs.length; i++) {
        const cmpnCnt = (pairs[i].origin.reservationCompanions || []).length;
        const visitCnt = pairs[i].target.visitCnt;

        if (visitCnt !== null && visitCnt !== undefined && cmpnCnt > visitCnt) {
          this.errorToast(
            `${pairs[i].courseName} ${pairs[i].origin.resveTime} 타임에 동반자 ${cmpnCnt}명이 입력되어 있습니다.<br>` +
              `${visitCnt}인 타임(${pairs[i].target.resveTime})으로 이동할 수 없습니다.`
          );
          return;
        }
      }

      if (
        !(await this.confirm(
          `${this.teamCount}팀을 ${this.selectedBlock.resveDateCaption} ` +
            `${this.selectedBlock.shapeCaption} 으로 이동하시겠습니까?`
        ))
      ) {
        return;
      }

      const moves = pairs.map((pair) => ({
        existTimeId: pair.origin.timeId,
        existOptFlag: pair.origin.optFlag,
        existResveDate: pair.origin.resveDate,
        moveTimeId: pair.target.timeId,
        moveOptFlag: pair.target.optFlag,
        moveResveDate: pair.target.resveDate,
      }));

      await GolfErpAPI.reservationTimeMoveAll({ moves });

      if (await this.confirm("예약 문자를 전송하시겠습니까?")) {
        await this.autoSendSms();
      }

      this.$emit("popupClosed", { isReload: true, block: this.selectedBlock });
      this.$refs.reservationMoveAllPopup.hide();
    },
    // 코스별 구간을 한 줄로 조립한다.
    // 코스마다 시작 간격이 다를 수 있으므로 코스별로 자기 구간을 따로 적는다.
    //   예) "어등 15:37~15:51 / 송정 15:30~15:51"
    buildCourseTimeCaption(courseList) {
      return (courseList || [])
        .map((course) => {
          const times = course.times || [];

          if (times.length === 0) {
            return course.courseName;
          }

          const firstTime = times[0].resveTime;
          const lastTime = times[times.length - 1].resveTime;

          return `${course.courseName} ${
            firstTime === lastTime ? firstTime : `${firstTime}~${lastTime}`
          }`;
        })
        .join(" / ");
    },
    // 단체 1건 발송. 팀마다 보내지 않고 수신자별로 1통씩만 전송한다.
    // 단체 전용 양식이 없어 단건 예약이동과 같은 G_CHANGE 를 쓰되,
    // 코스/시간 치환값에 전체 구간을 합쳐 넣어 한 통으로 전달한다.
    async autoSendSms() {
      if (!this.selectedBlock) {
        return;
      }

      const originDate = this.originDate;
      const targetDate = this.selectedBlock.resveDate;
      const teamCntCaption = `(${this.teamCount}팀)`;

      const baseData = {
        sendKey: this.popupData.resveNo,
        resveDate: originDate,
        dwName: getDayOfWeekCaption(moment(originDate).toDate()),
        // [FROM_COURSE]$ / [TO_COURSE]$ 자리에 코스별 구간을 전부 담는다.
        resveCourse: this.buildCourseTimeCaption(this.originGroups),
        // [FROM_TIME]$ / [TO_TIME]$ 자리에는 팀 수를 둔다.
        resveTime: teamCntCaption,
        toResveDate: targetDate,
        toDwName: getDayOfWeekCaption(moment(targetDate).toDate()),
        toResveCourse: this.buildCourseTimeCaption(this.selectedBlock.courses),
        toResveTime: teamCntCaption,
        transFlag: false,
      };

      // 같은 번호로 여러 번 가지 않도록 연락처 기준으로 중복을 제거한다.
      const smsSendList = [];
      const addedContactTels = new Set();

      const addReceiver = (contactTel, receiverName, memberDiv, transFlag) => {
        if (!contactTel || addedContactTels.has(contactTel)) {
          return;
        }

        addedContactTels.add(contactTel);

        smsSendList.push(
          Object.assign({}, baseData, {
            transFlag: !!transFlag,
            receiverName: receiverName,
            name:
              receiverName +
              " " +
              commonCodesGetCommonCodeAttrbNameByCodeAndIdx(
                "MEMBER_DIV",
                memberDiv,
                1
              ),
            contactTel: contactTel,
          })
        );
      };

      const resveName = this.popupData.resveName;
      const resveMemberDiv = this.popupData.resveMemberDiv;

      if (this.popupData.smsRecptnDiv === "A") {
        addReceiver(this.popupData.resveContactTel, resveName, resveMemberDiv, false);
        addReceiver(this.popupData.resveMngrHp, resveName, resveMemberDiv, false);
      } else if (this.popupData.smsRecptnDiv === "B") {
        addReceiver(this.popupData.resveMngrHp, resveName, resveMemberDiv, false);
      } else {
        addReceiver(this.popupData.resveContactTel, resveName, resveMemberDiv, false);
      }

      // 위임자·동반자는 팀마다 다를 수 있으므로 단체 전 팀에서 모은다.
      this.originTimes.forEach((timeInfo) => {
        if (timeInfo.transName && timeInfo.transName.trim() !== "") {
          addReceiver(
            timeInfo.contactTel,
            timeInfo.transName,
            timeInfo.memberDiv,
            true
          );
        }

        (timeInfo.reservationCompanions || []).forEach((companion) => {
          addReceiver(companion.contactTel, resveName, resveMemberDiv, false);
        });
      });

      if (!(smsSendList.length > 0)) {
        return;
      }

      const smsSendInfo = {
        type: "G_CHANGE",
        bizName: commonCodesGetStandardInfo("bizName"),
        bizNameLocation: commonCodesGetStandardInfo("bizNameLocation"),
        kakaoSendFlag: false,
        smsKind: null,
        kakaoDispatchKey: commonCodesGetStandardInfo("kakaoDsptchKey"),
        kakaoTemplateCode: null,
        subject: null,
      };

      const smsWords = await GolfErpAPI.fetchAllSmsWords();

      const smsSendType = smsWords.find(
        (data) => data.smsSendType === smsSendInfo.type
      );

      let message =
        (smsSendType.smsWords != null ? smsSendType.smsWords : "") +
        (smsSendType.aditWords != null ? "\n" + smsSendType.aditWords : "");

      if (smsSendInfo.bizNameLocation === "DOWN") {
        message =
          message +
          (smsSendInfo.bizName != null ? "\n" + smsSendInfo.bizName : "");
      }

      smsSendInfo.kakaoSendFlag = smsSendType.kakaoSendFlag;
      smsSendInfo.kakaoTemplateCode = smsSendType.kakaoTmplatCode;
      smsSendInfo.smsKind = smsSendType.smsKind;
      smsSendInfo.subject = smsSendType.smsSendTypeName;

      const smsRequestList = smsSendList.map((data) => ({
        type: smsSendInfo.type,
        kakaoFlag: smsSendInfo.kakaoSendFlag,
        smsKind: smsSendInfo.kakaoSendFlag ? smsSendInfo.smsKind : null,
        kakaoDispatchKey: smsSendInfo.kakaoSendFlag
          ? smsSendInfo.kakaoDispatchKey
          : null,
        kakaoTemplateKey: smsSendInfo.kakaoSendFlag
          ? smsSendInfo.kakaoTemplateCode
          : null,
        key: data.sendKey,
        reservedFlag: false,
        reserveDateTime: null,
        receiverName: data.receiverName,
        receiverNumber: data.contactTel,
        subject: smsSendInfo.subject,
        message: message,
        replaceDataMap: {
          name: data.name,
          resveDate: data.resveDate,
          dwName: data.dwName,
          resveCourse: data.resveCourse,
          resveTime: data.resveTime,
          resveFromDate: data.resveDate,
          resveToDate: data.toResveDate,
          fromDwName: data.dwName,
          toDwName: data.toDwName,
          resveFromCourse: data.resveCourse,
          resveToCourse: data.toResveCourse,
          resveFromTime: data.resveTime,
          resveToTime: data.toResveTime,
          transResveDiv: data.transFlag === true ? "위임예약" : "예약",
          headerMemo:
            smsSendInfo.bizNameLocation === "UP"
              ? smsSendInfo.bizName
                ? smsSendInfo.bizName
                : null
              : null,
        },
        dispatchNumber: smsSendType.dsptchNo,
        sender: "ERP",
      }));

      await GolfErpAPI.sendSMS(smsRequestList);
    },
  },
};
</script>

<style scoped>
/*
  공통 CSS(common.lazy.css)가 .article-section 에 height:100% 를 주기 때문에
  한 article 안에 섹션이 3개면 높이가 잡히지 않아 그리드가 렌더되지 않는다.
  섹션별 높이를 여기서 지정한다. (그리드는 .section-body 높이를 상속받는다)
*/
body .window.reservationMoveAll .windowContent .article-section.section-01 {height: auto;}
body .window.reservationMoveAll .windowContent .article-section.section-01 .section-body {overflow: visible; border: none;}
body .window.reservationMoveAll .windowContent .article-section.section-01 .body-box {padding: 6px 0; color: #000; background-color: #f9f9f9;}
body .window.reservationMoveAll .windowContent .article-section.section-01 .body-box ul {display: block; list-style: none; padding: 0; margin: 0;}
@supports (display: flow-root) {body .window.reservationMoveAll .windowContent .article-section.section-01 .body-box ul {display: flow-root;}}
body .window.reservationMoveAll .windowContent .article-section.section-01 .body-box ul:after {display: block; clear: both; height: 0; font-size: 0; line-height: 0; content: '';}
body .window.reservationMoveAll .windowContent .article-section.section-01 .body-box ul li {float: left; padding: 3px 12px 3px 13px; margin: 0 0 -1px 0; background: transparent url("~@/assets/images/common/division.png") no-repeat left center;}
body .window.reservationMoveAll .windowContent .article-section.section-01 .body-box ul li:first-child {padding: 3px 12px; background: none;}

body .window.reservationMoveAll .windowContent .article-section.section-02 {height: 300px;}
body .window.reservationMoveAll .windowContent .article-section.section-02 .section-body {overflow: hidden; border: none; padding: 0 1px;}

body .window.reservationMoveAll .windowContent .article-section.section-03 {height: calc(100% - 76px - 300px);}
body .window.reservationMoveAll .windowContent .article-section.section-03 .section-body {overflow: hidden; border: none; padding: 0 1px;}
</style>
